/**
 * Shared utility for resetting config to operator defaults
 * Used by useCommercialsConfig, useCommunicationsConfig, useJourneyConfig
 */

import { useCallback } from 'react';
import { isNoDebtTypeCohort } from '@/lib/debt-type-cohort';
import { deleteConfig } from '@/services/config';
import { type Client, type DebtType, type DomainT, type Operator, Scope, type ScopeT } from '@/types';

/**
 * The debt type a config scope should be keyed by, or null to fall back to the client.
 *
 * "Others" is a synthetic cohort standing for `debt_type_id IS NULL`, not a row in
 * `debt_types`. Those cases already resolve to the client config through the
 * inheritance chain, so configuring them *is* configuring the client — and keying a
 * fragment by the sentinel would violate the debt_type_id foreign key.
 */
function configDebtType(debtType: DebtType | null): DebtType | null {
  return debtType && !isNoDebtTypeCohort(debtType) ? debtType : null;
}

/**
 * Build scope key for config lookup
 * Structure: operator > client > debt_type (debt type sits within client)
 */
export function buildScopeKey(operatorId: string, clientId?: string, debtTypeId?: string): string {
  if (debtTypeId && !isNoDebtTypeCohort({ id: debtTypeId })) {
    return `${operatorId}:${debtTypeId}`;
  }
  if (clientId) {
    return `${operatorId}:${clientId}`;
  }
  return operatorId;
}

/**
 * Map scope type to ScopeT string literal
 */
export function getScopeType(debtType: DebtType | null, client: Client | null): ScopeT {
  if (configDebtType(debtType)) return Scope.DEBT_TYPE;
  if (client) return Scope.CLIENT;
  return Scope.OPERATOR;
}

/**
 * Owner IDs for a config write, with the "Others" cohort resolved to its client.
 * Config hooks pass their raw context through this before hitting the API.
 */
export function configScopeIds(
  debtType: DebtType | null,
  client: Client | null
): { debtTypeId: string | undefined; clientId: string | undefined } {
  return { debtTypeId: configDebtType(debtType)?.id, clientId: client?.id };
}

type LoadConfigFn<T> = (operator: Operator | null, debtType: DebtType | null, client: Client | null, bypassCache?: boolean) => Promise<T>;

type ClearCacheFn = (scopeKey: string) => void;

/**
 * Factory hook for creating a resetToOperatorDefaults function
 * Shared logic across all config hooks
 *
 * @param domain - The configuration domain (COMMERCIALS, COMMUNICATIONS, etc.)
 * @param loadConfigForContext - Function to load config for a given context
 * @param clearCacheEntry - Function to clear a cache entry by scope key
 * @returns A memoized resetToOperatorDefaults function
 */
export function useResetToOperatorDefaults<T>(domain: DomainT, loadConfigForContext: LoadConfigFn<T>, clearCacheEntry: ClearCacheFn) {
  return useCallback(
    async (operator: Operator | null, debtType: DebtType | null, client: Client | null): Promise<T> => {
      if (!operator) {
        console.warn('Cannot reset config: no operator selected');
        return {} as T;
      }

      const scope = getScopeType(debtType, client);

      // Only allow reset at client or debt type scope
      if (scope === Scope.OPERATOR) {
        throw new Error('Cannot reset operator-level config');
      }

      try {
        const ownerId = configDebtType(debtType)?.id ?? client?.id;
        if (!ownerId) {
          throw new Error('No client or debt type selected');
        }

        // Delete the config at this scope
        await deleteConfig(domain, scope, ownerId);

        // Clear cache for this scope
        const scopeKey = buildScopeKey(operator.id, client?.id, debtType?.id);
        clearCacheEntry(scopeKey);

        // Load the inherited config (operator level) with cache bypass
        const inheritedConfig = await loadConfigForContext(operator, null, null, true);
        return inheritedConfig;
      } catch (error) {
        console.error('Error resetting to operator defaults:', error);
        throw error;
      }
    },
    [domain, loadConfigForContext, clearCacheEntry]
  );
}
